import { Injectable } from '@angular/core';
import { WizardStateService } from './wizard-state.service';
import { WizardStep } from '../constants/wizard-steps.constants';
import { AiGenerationMode } from '../constants/ai-modes.constants';

@Injectable({
  providedIn: 'root'
})
export class WizardEngineService {

  constructor(private stateService: WizardStateService) { }

  nextStep(): void {
    const currentState = this.stateService.currentState;

    // Step 2 (UserNotes) + mapIdPreset → skip Upload step, go straight to ResourceConfig
    if (currentState.currentStep === WizardStep.UserNotes && currentState.mapIdPreset) {
      this.stateService.updateState({ currentStep: WizardStep.ResourceConfig });
      return;
    }

    // Step 5 (AiModeSelection) + Manual → go to Step 5a (ManualConfirm)
    if (currentState.currentStep === WizardStep.AiModeSelection && currentState.aiMode === AiGenerationMode.Manual) {
      this.stateService.updateState({ currentStep: WizardStep.ManualConfirm });
      return;
    }

    // Step 5 (AiModeSelection) + AI Assisted → go to Step 5b (AiModeConfirm)
    if (currentState.currentStep === WizardStep.AiModeSelection && currentState.aiMode === AiGenerationMode.AiAssisted) {
      this.stateService.updateState({ currentStep: WizardStep.AiModeConfirm });
      return;
    }

    // Step 5 (AiModeSelection) + Guided → go to Step 5c (GuidedConfirm)
    if (currentState.currentStep === WizardStep.AiModeSelection && currentState.aiMode === AiGenerationMode.Guided) {
      this.stateService.updateState({ currentStep: WizardStep.GuidedConfirm });
      return;
    }

    // Step 5a (ManualConfirm) → Proceed directly to Step 12 (ReviewCreate)
    // Manual mode never uses worldIdentity/globalContext/entity data (see WizardEngineService docs),
    // so steps 6-11 are skipped entirely.
    if (currentState.currentStep === WizardStep.ManualConfirm) {
      this.stateService.updateState({ currentStep: WizardStep.ReviewCreate });
      return;
    }

    // Step 5b (AiModeConfirm) → Proceed to Step 6 (WorldIdentity)
    if (currentState.currentStep === WizardStep.AiModeConfirm) {
      this.stateService.updateState({ currentStep: WizardStep.WorldIdentity });
      return;
    }

    // Step 5c (GuidedConfirm) → Proceed to Step 5d (GuidedConfirm2)
    if (currentState.currentStep === WizardStep.GuidedConfirm) {
      this.stateService.updateState({ currentStep: WizardStep.GuidedConfirm2 });
      return;
    }

    // Step 5d (GuidedConfirm2) → Proceed to Step 6 (WorldIdentity)
    if (currentState.currentStep === WizardStep.GuidedConfirm2) {
      this.stateService.updateState({ currentStep: WizardStep.WorldIdentity });
      return;
    }

    // Step 7 (GlobalContext) → Proceed to Step 7b (GlobalContext2)
    if (currentState.currentStep === WizardStep.GlobalContext) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext2 });
      return;
    }

    // Step 7b (GlobalContext2) → Proceed to Step 7c (GlobalContext3)
    if (currentState.currentStep === WizardStep.GlobalContext2) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext3 });
      return;
    }

    // Step 7c (GlobalContext3) → Proceed to Step 7d (GlobalContext4)
    if (currentState.currentStep === WizardStep.GlobalContext3) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext4 });
      return;
    }

    // Step 7d (GlobalContext4) → Proceed to Step 12 (ReviewCreate) or Step 8 (Cultures) based on AI Mode
    // (Manual mode never reaches this step — it skips 6-11 entirely from ManualConfirm)
    if (currentState.currentStep === WizardStep.GlobalContext4) {
      if (currentState.aiMode === AiGenerationMode.AiAssisted) {
        this.stateService.updateState({ currentStep: WizardStep.ReviewCreate });
      } else {
        this.stateService.updateState({ currentStep: WizardStep.Cultures });
      }
      return;
    }

    const nextStep = (currentState.currentStep as number) + 1;

    // Safety check
    if (nextStep > WizardStep.ReviewCreate) {
      return;
    }

    this.stateService.updateState({ currentStep: nextStep as WizardStep });
  }

  previousStep(): void {
    const currentState = this.stateService.currentState;

    // Step 4 (ResourceConfig) + mapIdPreset → skip back over Upload step to UserNotes
    if (currentState.currentStep === WizardStep.ResourceConfig && currentState.mapIdPreset) {
      this.stateService.updateState({ currentStep: WizardStep.UserNotes });
      return;
    }

    // Step 12 (ReviewCreate) + Manual → back to Step 5a (ManualConfirm)
    if (currentState.currentStep === WizardStep.ReviewCreate && currentState.aiMode === AiGenerationMode.Manual) {
      this.stateService.updateState({ currentStep: WizardStep.ManualConfirm });
      return;
    }

    // Step 12 (ReviewCreate) + AI Assisted → back to Step 7d (GlobalContext4)
    if (currentState.currentStep === WizardStep.ReviewCreate && currentState.aiMode === AiGenerationMode.AiAssisted) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext4 });
      return;
    }

    // Step 5a (ManualConfirm) → back to Step 5 (AiModeSelection)
    if (currentState.currentStep === WizardStep.ManualConfirm) {
      this.stateService.updateState({ currentStep: WizardStep.AiModeSelection });
      return;
    }

    // Step 5b (AiModeConfirm) → back to Step 5 (AiModeSelection)
    if (currentState.currentStep === WizardStep.AiModeConfirm) {
      this.stateService.updateState({ currentStep: WizardStep.AiModeSelection });
      return;
    }

    // Step 5c (GuidedConfirm) → back to Step 5 (AiModeSelection)
    if (currentState.currentStep === WizardStep.GuidedConfirm) {
      this.stateService.updateState({ currentStep: WizardStep.AiModeSelection });
      return;
    }

    // Step 5d (GuidedConfirm2) → back to Step 5c (GuidedConfirm)
    if (currentState.currentStep === WizardStep.GuidedConfirm2) {
      this.stateService.updateState({ currentStep: WizardStep.GuidedConfirm });
      return;
    }

    // Step 6 (WorldIdentity) + AI Assisted → back to Step 5b (AiModeConfirm)
    // (Manual mode never reaches this step — it skips 6-11 entirely from ManualConfirm)
    if (currentState.currentStep === WizardStep.WorldIdentity && currentState.aiMode === AiGenerationMode.AiAssisted) {
      this.stateService.updateState({ currentStep: WizardStep.AiModeConfirm });
      return;
    }

    // Step 8 (Cultures) → back to Step 7d (GlobalContext4)
    if (currentState.currentStep === WizardStep.Cultures) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext4 });
      return;
    }

    // Step 7d (GlobalContext4) → back to Step 7c (GlobalContext3)
    if (currentState.currentStep === WizardStep.GlobalContext4) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext3 });
      return;
    }

    // Step 7c (GlobalContext3) → back to Step 7b (GlobalContext2)
    if (currentState.currentStep === WizardStep.GlobalContext3) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext2 });
      return;
    }

    // Step 7b (GlobalContext2) → back to Step 7 (GlobalContext)
    if (currentState.currentStep === WizardStep.GlobalContext2) {
      this.stateService.updateState({ currentStep: WizardStep.GlobalContext });
      return;
    }

    const prevStep = (currentState.currentStep as number) - 1;

    // Safety check
    if (prevStep < WizardStep.Welcome) {
      return;
    }

    this.stateService.updateState({ currentStep: prevStep as WizardStep });
  }
}
